import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type Rating } from '@avanta/shared/types/psychologists';
import { type TableFilterOption } from '@avanta/shared/types/table';
import { type ColumnDef } from '@tanstack/react-table';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFetchRating } from './useFetchRating';

const RATING_FILTER_OPTIONS: TableFilterOption<number>[] = Array.from(
  { length: 5 },
  (_, index) => ({
    key: index + 1,
    label: (
      <>
        <Star size={16} className="fill-denim-600" />
        {index + 1}
      </>
    ),
  })
);

const getTableColumns = (): ColumnDef<Rating>[] => [
  {
    id: 'rate',
    accessorKey: 'rate',
    header: t('PsychologistRatings.Table.rate.label', { ns: LOCIZE_NS.PROFILE }),
    cell: ({ row }) => (
      <span className="line-clamp-2 flex items-center gap-2">
        <Star size={16} className="fill-denim-600" />
        {String(row.original.rate)}
      </span>
    ),
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: t('PsychologistRatings.Table.description.label', { ns: LOCIZE_NS.PROFILE }),
    cell: ({ getValue }) => <span className="line-clamp-2">{String(getValue())}</span>,
  },
];

const PAGE_SIZE = 10;

export const RatingsTable = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.PROFILE);
  const { ratingList = [], isLoading } = useFetchRating();

  return (
    <div className="flex flex-col gap-4">
      <ClientPaginatedTable
        data={ratingList}
        columns={getTableColumns()}
        emptyMessage={t('PsychologistRatings.empty')}
        filter={{
          accessorKey: 'rate',
          options: RATING_FILTER_OPTIONS,
        }}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
      />
    </div>
  );
};
